<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { DOCUMENT_TYPE, isDocumentEditable, type DocumentType } from '@hsl-ems/contracts';

import {
  acceptedMime,
  p2Api,
  sha256Hex,
  type BankAccount,
  type AvailableLoan,
  type DocumentFileLink,
  type DocumentRecord,
  type ExpenseCategory,
  type FileMetadata,
  type OperatingOrganization,
  type Vendor,
} from '@/lib/p2-api';
import { p3Api, type ApprovalTaskSummary } from '@/lib/p3-api';

type DocumentSlug = 'reimbursement' | 'loan' | 'cash-repayment' | 'corporate-payment';
interface ExpenseItemForm {
  key: string;
  categoryId: string;
  incurredOn: string;
  description: string;
  amount: string;
  city: string;
  remark: string;
}
interface LoanOffsetForm {
  key: string;
  loanDocumentId: string;
  amount: string;
}

const route = useRoute();
const router = useRouter();
const slug = computed(() => route.params.kind as DocumentSlug);
const typeBySlug: Record<DocumentSlug, DocumentType> = {
  reimbursement: DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT,
  loan: DOCUMENT_TYPE.LOAN,
  'cash-repayment': DOCUMENT_TYPE.CASH_REPAYMENT,
  'corporate-payment': DOCUMENT_TYPE.CORPORATE_PAYMENT,
};
const titleBySlug: Record<DocumentSlug, string> = {
  reimbursement: '费用报销',
  loan: '借款申请',
  'cash-repayment': '现金还款',
  'corporate-payment': '对公付款',
};
const type = computed(() => typeBySlug[slug.value]);
const documentId = computed(() =>
  typeof route.params.id === 'string' ? route.params.id : undefined,
);
const document = ref<DocumentRecord>();
const organizations = ref<OperatingOrganization[]>([]);
const categories = ref<ExpenseCategory[]>([]);
const bankAccounts = ref<BankAccount[]>([]);
const vendors = ref<Vendor[]>([]);
const availableLoans = ref<AvailableLoan[]>([]);
const loading = ref(true);
const saving = ref(false);
const submitting = ref(false);
const dirty = ref(false);
const error = ref('');
const notice = ref('');
const approvalTasks = ref<ApprovalTaskSummary[]>([]);

const operatingOrgId = ref('');
const title = ref('');
const description = ref('');
const bankAccountId = ref('');
const amount = ref('');
const plannedUseDate = ref('');
const plannedRepaymentDate = ref('');
const loanDocumentId = ref('');
const categoryId = ref('');
const vendorId = ref('');
const vendorBankAccountId = ref('');
const expectedPaymentDate = ref('');
const referenceNo = ref('');
const items = ref<ExpenseItemForm[]>([newExpenseItem()]);
const offsets = ref<LoanOffsetForm[]>([]);

const showAccountForm = ref(false);
const newBankName = ref('');
const newAccountNumber = ref('');
const newAccountDefault = ref(false);
const accountSaving = ref(false);

const uploadPurpose = ref('RECEIPT');
const uploading = ref(false);
const uploadMessage = ref('');

const editable = computed(() => !document.value || isDocumentEditable(document.value.status));
const filteredCategories = computed(() =>
  categories.value.filter(
    (category) => !category.organizationId || category.organizationId === operatingOrgId.value,
  ),
);
const filteredVendors = computed(() =>
  vendors.value.filter((vendor) => vendor.organizationId === operatingOrgId.value),
);
const selectedVendor = computed(() =>
  filteredVendors.value.find((vendor) => vendor.id === vendorId.value),
);
const offsetTotal = computed(() =>
  offsets.value.reduce((sum, offset) => sum + (Number(offset.amount) || 0), 0),
);
const reimbursementPayable = computed(() => Math.max(0, expenseTotal.value - offsetTotal.value));
const attachments = computed(() => document.value?.files ?? []);
const attachmentsReady = computed(() =>
  attachments.value.every((link) => link.file.status === 'CLEAN'),
);
const canSubmit = computed(
  () =>
    Boolean(document.value) &&
    editable.value &&
    !dirty.value &&
    attachmentsReady.value &&
    !saving.value &&
    !submitting.value,
);
const expenseTotal = computed(() =>
  items.value.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
);

const statusLabels = {
  DRAFT: '草稿',
  IN_APPROVAL: '审批中',
  RETURNED: '已退回',
  REJECTED: '已驳回',
  APPROVED: '已批准',
  COMPLETED: '已完成',
  VOIDED: '已作废',
  CORRECTED: '已更正',
} as const;

onMounted(load);
onBeforeRouteLeave(
  () =>
    !dirty.value ||
    saving.value ||
    submitting.value ||
    window.confirm('当前修改尚未保存，确定离开吗？'),
);

async function load() {
  if (!type.value) return void (await router.replace('/documents'));
  loading.value = true;
  error.value = '';
  try {
    const [orgResult, categoryResult, accountResult, vendorResult] = await Promise.all([
      p2Api.operatingOrganizations(),
      p2Api.categories(),
      p2Api.bankAccounts(),
      p2Api.vendors(),
    ]);
    organizations.value = orgResult;
    categories.value = categoryResult;
    bankAccounts.value = accountResult;
    vendors.value = vendorResult;
    if (documentId.value) {
      document.value = await p2Api.get(type.value, documentId.value);
      populate(document.value);
      if (document.value.status === 'IN_APPROVAL') await loadApprovalTasks();
    } else {
      operatingOrgId.value =
        organizations.value.find((item) => item.isPrimary)?.id ?? orgResult[0]?.id ?? '';
      bankAccountId.value =
        bankAccounts.value.find((item) => item.isDefault)?.id ?? accountResult[0]?.id ?? '';
    }
    await refreshAvailableLoans();
    await nextTick();
    dirty.value = false;
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : '页面加载失败';
    if (message.includes('登录已失效')) return void (await router.replace('/login'));
    error.value = message;
  } finally {
    loading.value = false;
  }
}

async function loadApprovalTasks() {
  if (!document.value) return;
  const result = await p3Api.documentTasks(document.value.id);
  approvalTasks.value = result.tasks;
}

function populate(record: DocumentRecord) {
  operatingOrgId.value = record.operatingOrgId;
  description.value = record.description;
  amount.value = record.requestedAmount;
  if (record.reimbursement) {
    title.value = record.reimbursement.title;
    bankAccountId.value = record.reimbursement.bankAccount.id;
    items.value = record.reimbursement.items.map((item) => ({
      key: item.id,
      categoryId: item.categoryId,
      incurredOn: item.incurredOn.slice(0, 10),
      description: item.description,
      amount: item.amount,
      city: item.city ?? '',
      remark: item.remark ?? '',
    }));
    offsets.value = record.reimbursementOffsets.map((offset) => ({
      key: offset.id,
      loanDocumentId: offset.loanDocumentId,
      amount: offset.amount,
    }));
  }
  if (record.loan) {
    description.value = record.loan.purpose;
    bankAccountId.value = record.loan.bankAccount.id;
    plannedUseDate.value = record.loan.plannedUseDate.slice(0, 10);
    plannedRepaymentDate.value = record.loan.plannedRepaymentDate.slice(0, 10);
  }
  if (record.cashRepayment) {
    loanDocumentId.value = record.cashRepayment.loanDocumentId;
    amount.value = record.cashRepayment.amount;
    description.value = record.cashRepayment.note ?? '';
  }
  if (record.corporatePayment) {
    categoryId.value = record.corporatePayment.categoryId;
    vendorId.value = record.corporatePayment.vendorId;
    vendorBankAccountId.value = record.corporatePayment.vendorBankAccountId;
    description.value = record.corporatePayment.purpose;
    expectedPaymentDate.value = record.corporatePayment.expectedPaymentDate.slice(0, 10);
    referenceNo.value = record.corporatePayment.referenceNo ?? '';
  }
}

async function saveDraft() {
  if (!type.value || !operatingOrgId.value) return;
  saving.value = true;
  error.value = '';
  notice.value = '';
  try {
    const payload = buildPayload();
    const saved = document.value
      ? await p2Api.update(type.value, document.value.id, {
          ...payload,
          version: document.value.version,
        })
      : await p2Api.create(type.value, { operatingOrgId: operatingOrgId.value, ...payload });
    document.value = saved;
    populate(saved);
    dirty.value = false;
    notice.value = `草稿 ${saved.documentNo} 已保存`;
    if (!documentId.value) await router.replace(`/documents/${slug.value}/${saved.id}`);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '草稿保存失败';
  } finally {
    saving.value = false;
  }
}

function buildPayload(): Record<string, unknown> {
  if (type.value === DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT)
    return {
      title: title.value,
      description: description.value,
      bankAccountId: bankAccountId.value,
      items: items.value.map((item) => ({
        categoryId: item.categoryId,
        incurredOn: item.incurredOn,
        description: item.description,
        amount: normalizeMoney(item.amount),
        ...(item.city ? { city: item.city } : {}),
        ...(item.remark ? { remark: item.remark } : {}),
      })),
      offsets: offsets.value.map((offset) => ({
        loanDocumentId: offset.loanDocumentId,
        amount: normalizeMoney(offset.amount),
      })),
    };
  if (type.value === DOCUMENT_TYPE.LOAN)
    return {
      purpose: description.value,
      amount: normalizeMoney(amount.value),
      bankAccountId: bankAccountId.value,
      plannedUseDate: plannedUseDate.value,
      plannedRepaymentDate: plannedRepaymentDate.value,
    };
  if (type.value === DOCUMENT_TYPE.CASH_REPAYMENT)
    return {
      loanDocumentId: loanDocumentId.value,
      amount: normalizeMoney(amount.value),
      ...(description.value ? { note: description.value } : {}),
    };
  return {
    categoryId: categoryId.value,
    vendorId: vendorId.value,
    vendorBankAccountId: vendorBankAccountId.value,
    purpose: description.value,
    amount: normalizeMoney(amount.value),
    expectedPaymentDate: expectedPaymentDate.value,
    ...(referenceNo.value ? { referenceNo: referenceNo.value } : {}),
  };
}

function normalizeMoney(value: string) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : value;
}

function newExpenseItem(): ExpenseItemForm {
  return {
    key: crypto.randomUUID(),
    categoryId: '',
    incurredOn: new Date().toISOString().slice(0, 10),
    description: '',
    amount: '',
    city: '',
    remark: '',
  };
}

function addItem() {
  items.value.push(newExpenseItem());
  dirty.value = true;
}

function removeItem(index: number) {
  if (items.value.length === 1) return;
  items.value.splice(index, 1);
  dirty.value = true;
}

function addOffset() {
  offsets.value.push({ key: crypto.randomUUID(), loanDocumentId: '', amount: '' });
  dirty.value = true;
}

function removeOffset(index: number) {
  offsets.value.splice(index, 1);
  dirty.value = true;
}

async function refreshAvailableLoans() {
  if (!operatingOrgId.value) {
    availableLoans.value = [];
    return;
  }
  try {
    availableLoans.value = await p2Api.availableLoans(operatingOrgId.value);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '可冲销借款加载失败';
  }
}

async function createBankAccount() {
  accountSaving.value = true;
  error.value = '';
  try {
    const created = await p2Api.createBankAccount({
      bankName: newBankName.value,
      accountNumber: newAccountNumber.value,
      isDefault: newAccountDefault.value,
    });
    bankAccounts.value = newAccountDefault.value
      ? [created, ...bankAccounts.value.map((account) => ({ ...account, isDefault: false }))]
      : [created, ...bankAccounts.value];
    bankAccountId.value = created.id;
    newBankName.value = '';
    newAccountNumber.value = '';
    newAccountDefault.value = false;
    showAccountForm.value = false;
    dirty.value = true;
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '账户保存失败';
  } finally {
    accountSaving.value = false;
  }
}

async function uploadFile(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file || !document.value) return;
  const mimeType = acceptedMime(file);
  if (!mimeType) {
    uploadMessage.value = '文件类型或扩展名不受支持';
    return;
  }
  uploading.value = true;
  uploadMessage.value = `正在上传 ${file.name}…`;
  try {
    const hash = await sha256Hex(file);
    const intent = await p2Api.createUploadIntent({
      documentId: document.value.id,
      originalName: file.name,
      mimeType,
      sizeBytes: file.size,
      purpose: uploadPurpose.value,
    });
    const upload = await fetch(intent.uploadUrl, {
      method: 'PUT',
      headers: intent.requiredHeaders,
      body: file,
    });
    if (!upload.ok) throw new Error('对象存储上传失败');
    await p2Api.completeUpload(intent.id, hash);
    document.value = await p2Api.get(type.value, document.value.id);
    uploadMessage.value = '上传完成，正在进行安全扫描…';
    await pollFile(intent.id);
  } catch (reason) {
    uploadMessage.value = reason instanceof Error ? reason.message : '附件上传失败';
  } finally {
    uploading.value = false;
  }
}

async function pollFile(id: string) {
  for (let attempt = 0; attempt < 15; attempt += 1) {
    const metadata = await p2Api.file(id);
    replaceFile(metadata);
    if (metadata.status === 'CLEAN') {
      uploadMessage.value = '附件安全扫描通过';
      return;
    }
    if (['REJECTED', 'QUARANTINED', 'DELETED'].includes(metadata.status)) {
      uploadMessage.value = fileStatusLabel(metadata);
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 2000));
  }
  uploadMessage.value = '附件仍在扫描，可稍后点击刷新';
}

function replaceFile(metadata: FileMetadata) {
  const link = document.value?.files.find((item) => item.file.id === metadata.id);
  if (!link) return;
  link.file = {
    id: metadata.id,
    originalName: metadata.originalName,
    declaredMime: metadata.declaredMime,
    detectedMime: metadata.detectedMime,
    status: metadata.status,
    scanResult: metadata.scanResult,
    createdAt: metadata.createdAt,
    updatedAt: metadata.updatedAt,
  };
}

async function refreshFile(link: DocumentFileLink) {
  uploadMessage.value = '';
  try {
    replaceFile(await p2Api.file(link.file.id));
  } catch (reason) {
    uploadMessage.value = reason instanceof Error ? reason.message : '附件状态刷新失败';
  }
}

async function removeFile(link: DocumentFileLink) {
  if (!document.value || !window.confirm(`从草稿移除“${link.file.originalName}”吗？`)) return;
  try {
    const result = await p2Api.removeFile(link.file.id, document.value.id, document.value.version);
    document.value.version = result.documentVersion;
    document.value.files = document.value.files.filter((item) => item.file.id !== link.file.id);
    uploadMessage.value = '附件已从草稿移除，将按保留期清理';
  } catch (reason) {
    uploadMessage.value = reason instanceof Error ? reason.message : '附件移除失败';
  }
}

async function downloadFile(link: DocumentFileLink) {
  try {
    const { url } = await p2Api.downloadFile(link.file.id);
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (reason) {
    uploadMessage.value = reason instanceof Error ? reason.message : '附件下载失败';
  }
}

async function voidDraft() {
  if (!document.value || !type.value || !window.confirm('作废后不能继续编辑，确定作废吗？')) return;
  saving.value = true;
  try {
    document.value = await p2Api.void(type.value, document.value.id, document.value.version);
    dirty.value = false;
    notice.value = '单据已作废';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '作废失败';
  } finally {
    saving.value = false;
  }
}

async function submitDocument() {
  if (!document.value || !type.value || !canSubmit.value) return;
  if (!window.confirm('提交后业务字段和附件将锁定，并按当前规则创建审批待办。确定提交吗？')) return;
  submitting.value = true;
  error.value = '';
  notice.value = '';
  try {
    const result = await p3Api.submit(type.value, document.value.id, document.value.version);
    document.value.status = result.document.status;
    document.value.version = result.document.version;
    document.value.submittedAt = new Date().toISOString();
    dirty.value = false;
    await loadApprovalTasks();
    notice.value = `已提交至“${result.flow.name}”第 ${result.flow.versionNumber} 版，首节点生成 ${result.tasks.length} 个待办`;
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '提交失败';
  } finally {
    submitting.value = false;
  }
}

async function withdrawDocument() {
  if (!document.value || !type.value || document.value.status !== 'IN_APPROVAL') return;
  if (!window.confirm('仅在尚无审批任务被处理时可以撤回。确定撤回到草稿吗？')) return;
  submitting.value = true;
  error.value = '';
  notice.value = '';
  try {
    const result = await p3Api.withdraw(type.value, document.value.id, document.value.version);
    document.value.status = result.document.status;
    document.value.version = result.document.version;
    document.value.submittedAt = null;
    approvalTasks.value = [];
    dirty.value = false;
    notice.value = '单据已撤回，可继续修改后重新提交';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '撤回失败';
  } finally {
    submitting.value = false;
  }
}

function fileStatusLabel(metadata: Pick<FileMetadata, 'status' | 'scanResult'>) {
  const labels: Record<FileMetadata['status'], string> = {
    UPLOADING: '等待上传',
    PENDING_SCAN: '安全扫描中',
    CLEAN: '扫描通过',
    QUARANTINED: '文件已隔离',
    REJECTED: '文件已拒绝',
    DELETING: '正在清理',
    DELETED: '已清理',
  };
  return metadata.scanResult && metadata.status !== 'CLEAN'
    ? `${labels[metadata.status]}（${metadata.scanResult}）`
    : labels[metadata.status];
}
</script>

<template>
  <main class="workspace-shell editor-shell">
    <header class="workspace-header">
      <RouterLink class="workspace-brand" to="/documents">
        <span class="brand-mark" aria-hidden="true">郎</span>
        <span><strong>郎报通</strong><small>单据编辑</small></span>
      </RouterLink>
      <RouterLink class="text-link" to="/documents">← 返回单据列表</RouterLink>
    </header>

    <section class="editor-heading">
      <div>
        <p class="eyebrow">{{ document ? document.documentNo : '新建草稿' }}</p>
        <h1>{{ titleBySlug[slug] }}</h1>
        <p v-if="document">
          当前状态：<span class="status-pill" :data-status="document.status">{{
            statusLabels[document.status]
          }}</span>
          · 版本 {{ document.version }}
        </p>
      </div>
      <div class="editor-actions">
        <button
          v-if="document && editable"
          class="danger-button"
          type="button"
          :disabled="saving"
          @click="voidDraft"
        >
          作废
        </button>
        <button v-if="editable" type="submit" form="document-form" :disabled="saving">
          {{ saving ? '正在保存…' : '保存草稿' }}
        </button>
      </div>
    </section>

    <p v-if="error" class="inline-alert inline-alert--error" role="alert">{{ error }}</p>
    <p v-if="notice" class="inline-alert inline-alert--success" role="status">{{ notice }}</p>
    <section v-if="loading" class="document-empty">正在加载表单与主数据…</section>

    <form
      v-else
      id="document-form"
      class="document-form"
      @submit.prevent="saveDraft"
      @input="dirty = true"
      @change="dirty = true"
    >
      <fieldset class="form-section" :disabled="!editable">
        <legend>基本信息</legend>
        <div class="form-grid">
          <label>
            <span>归属组织</span>
            <select
              v-model="operatingOrgId"
              required
              :disabled="Boolean(document)"
              @change="refreshAvailableLoans"
            >
              <option value="" disabled>请选择经营组织</option>
              <option v-for="org in organizations" :key="org.id" :value="org.id">
                {{ org.name }}（{{ org.code }}）{{ org.isPrimary ? ' · 主归属' : '' }}
              </option>
            </select>
          </label>

          <label v-if="type === DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT">
            <span>报销标题</span>
            <input v-model="title" required maxlength="200" placeholder="例如：8 月采购差旅报销" />
          </label>

          <label v-if="type === DOCUMENT_TYPE.CORPORATE_PAYMENT">
            <span>费用类别</span>
            <select v-model="categoryId" required>
              <option value="" disabled>请选择费用类别</option>
              <option
                v-for="category in filteredCategories"
                :key="category.id"
                :value="category.id"
              >
                {{ category.code }} · {{ category.name }}
              </option>
            </select>
          </label>

          <label v-if="type === DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT" class="form-span-2">
            <span>报销说明</span>
            <textarea v-model="description" required minlength="5" maxlength="1000" rows="3" />
          </label>
          <label v-else-if="type === DOCUMENT_TYPE.LOAN" class="form-span-2">
            <span>借款用途</span>
            <textarea v-model="description" required minlength="5" maxlength="1000" rows="3" />
          </label>
          <label v-else-if="type === DOCUMENT_TYPE.CASH_REPAYMENT" class="form-span-2">
            <span>还款说明</span>
            <textarea v-model="description" maxlength="500" rows="3" />
          </label>
          <label v-else class="form-span-2">
            <span>付款事由</span>
            <textarea v-model="description" required minlength="5" maxlength="1000" rows="3" />
          </label>
        </div>
      </fieldset>

      <fieldset
        v-if="type === DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT"
        class="form-section"
        :disabled="!editable"
      >
        <legend>费用明细</legend>
        <article v-for="(item, index) in items" :key="item.key" class="expense-item">
          <div class="expense-item-heading">
            <strong>明细 {{ index + 1 }}</strong>
            <button class="text-button danger-text" type="button" @click="removeItem(index)">
              删除
            </button>
          </div>
          <div class="form-grid form-grid--items">
            <label
              ><span>发生日期</span><input v-model="item.incurredOn" type="date" required
            /></label>
            <label>
              <span>费用类别</span>
              <select v-model="item.categoryId" required>
                <option value="" disabled>请选择</option>
                <option
                  v-for="category in filteredCategories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}{{ category.requiresAttachment ? ' · 需附件' : '' }}
                </option>
              </select>
            </label>
            <label
              ><span>金额</span
              ><input v-model="item.amount" inputmode="decimal" required pattern="\d+(\.\d{1,2})?"
            /></label>
            <label><span>发生城市</span><input v-model="item.city" maxlength="100" /></label>
            <label class="form-span-2"
              ><span>明细说明</span><input v-model="item.description" required maxlength="500"
            /></label>
            <label class="form-span-2"
              ><span>备注</span><input v-model="item.remark" maxlength="500"
            /></label>
          </div>
        </article>
        <button class="quiet-button" type="button" @click="addItem">＋ 添加明细</button>
        <p class="amount-summary">
          报销合计 <strong>¥{{ expenseTotal.toFixed(2) }}</strong>
        </p>
      </fieldset>

      <fieldset
        v-if="type === DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT"
        class="form-section loan-offset-section"
        :disabled="!editable"
      >
        <legend>借款冲销（可选）</legend>
        <p class="section-hint">
          仅可选择本人同一经营组织的未结借款；提交与最终审批入账时都会重新校验余额。
        </p>
        <article v-for="(offset, index) in offsets" :key="offset.key" class="expense-item">
          <div class="expense-item-heading">
            <strong>冲销 {{ index + 1 }}</strong>
            <button class="text-button danger-text" type="button" @click="removeOffset(index)">
              删除
            </button>
          </div>
          <div class="form-grid">
            <label>
              <span>未结借款</span>
              <select v-model="offset.loanDocumentId" required>
                <option value="" disabled>请选择借款单</option>
                <option v-for="loan in availableLoans" :key="loan.id" :value="loan.id">
                  {{ loan.documentNo }} · 可用 ¥{{ loan.availableBalance }}
                </option>
              </select>
            </label>
            <label>
              <span>冲销金额</span>
              <input
                v-model="offset.amount"
                inputmode="decimal"
                required
                pattern="\d+(\.\d{1,2})?"
              />
            </label>
          </div>
        </article>
        <button class="quiet-button" type="button" @click="addOffset">＋ 选择借款冲销</button>
        <p class="amount-summary">
          冲销合计 <strong>¥{{ offsetTotal.toFixed(2) }}</strong> · 预计应付
          <strong>¥{{ reimbursementPayable.toFixed(2) }}</strong>
        </p>
        <p v-if="!availableLoans.length" class="section-hint">
          当前组织没有可用余额大于零的本人借款。
        </p>
      </fieldset>

      <fieldset v-if="type === DOCUMENT_TYPE.LOAN" class="form-section" :disabled="!editable">
        <legend>借款计划</legend>
        <div class="form-grid">
          <label
            ><span>借款金额</span
            ><input v-model="amount" inputmode="decimal" required pattern="\d+(\.\d{1,2})?"
          /></label>
          <label
            ><span>计划使用日</span><input v-model="plannedUseDate" type="date" required
          /></label>
          <label
            ><span>计划还款日</span><input v-model="plannedRepaymentDate" type="date" required
          /></label>
        </div>
      </fieldset>

      <fieldset
        v-if="type === DOCUMENT_TYPE.CASH_REPAYMENT"
        class="form-section"
        :disabled="!editable"
      >
        <legend>来源借款</legend>
        <div class="form-grid">
          <label>
            <span>已完成借款</span>
            <select v-model="loanDocumentId" required>
              <option value="" disabled>请选择借款单</option>
              <option v-for="loan in availableLoans" :key="loan.id" :value="loan.id">
                {{ loan.documentNo }} · 可用 ¥{{ loan.availableBalance }}
              </option>
            </select>
          </label>
          <label
            ><span>还款金额</span
            ><input v-model="amount" inputmode="decimal" required pattern="\d+(\.\d{1,2})?"
          /></label>
        </div>
        <p v-if="!availableLoans.length" class="section-hint">
          当前组织没有可申请现金还款的已完成借款。
        </p>
      </fieldset>

      <fieldset
        v-if="type === DOCUMENT_TYPE.CORPORATE_PAYMENT"
        class="form-section"
        :disabled="!editable"
      >
        <legend>供应商与付款</legend>
        <div class="form-grid">
          <label>
            <span>供应商</span>
            <select v-model="vendorId" required @change="vendorBankAccountId = ''">
              <option value="" disabled>请选择供应商</option>
              <option v-for="vendor in filteredVendors" :key="vendor.id" :value="vendor.id">
                {{ vendor.name }}
              </option>
            </select>
          </label>
          <label>
            <span>对公账户</span>
            <select v-model="vendorBankAccountId" required>
              <option value="" disabled>请选择收款账户</option>
              <option
                v-for="account in selectedVendor?.accounts ?? []"
                :key="account.id"
                :value="account.id"
              >
                {{ account.bankName }} · {{ account.accountMasked }}
              </option>
            </select>
          </label>
          <label
            ><span>付款金额</span
            ><input v-model="amount" inputmode="decimal" required pattern="\d+(\.\d{1,2})?"
          /></label>
          <label
            ><span>期望付款日</span><input v-model="expectedPaymentDate" type="date" required
          /></label>
          <label class="form-span-2"
            ><span>业务依据编号（可选）</span><input v-model="referenceNo" maxlength="100"
          /></label>
        </div>
      </fieldset>

      <fieldset
        v-if="type === DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT || type === DOCUMENT_TYPE.LOAN"
        class="form-section"
        :disabled="!editable"
      >
        <legend>本人收款账户</legend>
        <div class="account-options">
          <label v-for="account in bankAccounts" :key="account.id" class="account-option">
            <input v-model="bankAccountId" type="radio" :value="account.id" required />
            <span
              ><strong>{{ account.bankName }}</strong
              ><small>{{ account.holderName }} · {{ account.accountMasked }}</small></span
            >
            <em v-if="account.isDefault">默认</em>
          </label>
        </div>
        <button class="quiet-button" type="button" @click="showAccountForm = !showAccountForm">
          {{ showAccountForm ? '收起新增账户' : '＋ 新增本人账户' }}
        </button>
        <div v-if="showAccountForm" class="inline-form">
          <label
            ><span>开户银行</span><input v-model="newBankName" minlength="2" maxlength="120"
          /></label>
          <label
            ><span>银行卡号</span
            ><input
              v-model="newAccountNumber"
              inputmode="numeric"
              minlength="8"
              maxlength="40"
              autocomplete="off"
          /></label>
          <label class="check-label"
            ><input v-model="newAccountDefault" type="checkbox" /> 设为默认账户</label
          >
          <button
            type="button"
            :disabled="accountSaving || !newBankName || !newAccountNumber"
            @click="createBankAccount"
          >
            {{ accountSaving ? '正在保存…' : '保存账户' }}
          </button>
        </div>
      </fieldset>
    </form>

    <section v-if="!loading" class="form-section attachment-section">
      <div class="section-heading-row">
        <div>
          <h2>附件</h2>
          <p>支持 PDF、图片、DOCX、XLSX，单文件不超过 20 MiB。</p>
        </div>
        <div v-if="document && editable" class="upload-control">
          <select v-model="uploadPurpose" aria-label="附件用途">
            <option value="RECEIPT">票据</option>
            <option value="EVIDENCE">业务依据</option>
            <option value="REPAYMENT_PROOF">还款凭证</option>
            <option value="OTHER">其他</option>
          </select>
          <label class="upload-button">
            {{ uploading ? '上传处理中…' : '选择文件' }}
            <input
              type="file"
              :disabled="uploading"
              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.docx,.xlsx"
              @change="uploadFile"
            />
          </label>
        </div>
      </div>
      <p v-if="!document" class="section-hint">请先保存草稿，再上传附件。</p>
      <p v-if="uploadMessage" class="section-hint" aria-live="polite">{{ uploadMessage }}</p>
      <div v-if="attachments.length" class="attachment-list">
        <article v-for="link in attachments" :key="link.file.id" class="attachment-row">
          <span class="file-icon" aria-hidden="true">附</span>
          <span
            ><strong>{{ link.file.originalName }}</strong
            ><small>{{ link.purpose }} · {{ fileStatusLabel(link.file) }}</small></span
          >
          <button class="text-button" type="button" @click="refreshFile(link)">刷新</button>
          <button
            v-if="link.file.status === 'CLEAN'"
            class="text-button"
            type="button"
            @click="downloadFile(link)"
          >
            下载
          </button>
          <button
            v-if="editable"
            class="text-button danger-text"
            type="button"
            @click="removeFile(link)"
          >
            移除
          </button>
        </article>
      </div>
      <div v-else-if="document" class="compact-empty">尚未添加附件</div>
    </section>

    <section v-if="document" class="submit-gate">
      <div>
        <strong>{{ document.status === 'IN_APPROVAL' ? '审批进行中' : '提交审批' }}</strong>
        <p v-if="editable && dirty">请先保存当前修改，提交时系统会冻结已保存版本。</p>
        <p v-else-if="editable && !attachmentsReady">仍有附件未通过安全扫描，暂不能提交。</p>
        <p v-else-if="editable">提交后将匹配已发布流程，并锁定业务字段和附件。</p>
        <p v-else-if="document.status === 'IN_APPROVAL'">
          尚无任务被处理时可以撤回；审批开始处理后将禁止撤回。
        </p>
        <p v-else>当前状态不允许再次提交。</p>
      </div>
      <button v-if="editable" type="button" :disabled="!canSubmit" @click="submitDocument">
        {{ submitting ? '正在提交…' : '确认提交' }}
      </button>
      <button
        v-else-if="document.status === 'IN_APPROVAL'"
        class="danger-button"
        type="button"
        :disabled="submitting"
        @click="withdrawDocument"
      >
        {{ submitting ? '正在撤回…' : '撤回申请' }}
      </button>
    </section>

    <section v-if="document?.status === 'IN_APPROVAL'" class="form-section document-approval-panel">
      <div class="section-heading-row">
        <div>
          <h2>当前审批任务</h2>
          <p>任务详情可查看完整审批历史，并对当前待办发起催办。</p>
        </div>
        <button class="quiet-button" type="button" @click="loadApprovalTasks">刷新</button>
      </div>
      <div v-if="approvalTasks.length" class="approval-list compact-approval-list">
        <RouterLink
          v-for="taskItem in approvalTasks"
          :key="taskItem.id"
          class="approval-row"
          :to="`/approval-tasks/${taskItem.id}`"
        >
          <span class="document-icon">{{ taskItem.node.sequence }}</span>
          <span class="document-main">
            <strong>{{ taskItem.node.name }}</strong>
            <small>{{ taskItem.instance.document.applicant.name }} · {{ taskItem.status }}</small>
          </span>
          <span>查看与催办 ›</span>
        </RouterLink>
      </div>
      <div v-else class="compact-empty">审批实例尚未生成可见任务</div>
    </section>
  </main>
</template>
